import { yupResolver } from "@hookform/resolvers/yup";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Alert, Box, Button, IconButton, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { TableColumn } from "react-data-table-component";
import { Controller, useForm } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";
import { RootState } from "../../../../redux";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomModal from "../../../../ui/components/custom-modal/custom-modal";
import CustomSelect from "../../../../ui/components/custom-select/custom-select";
import DateInputFilter from "../../../../ui/components/date-input-filter/date-input-filter";
import DateInput from "../../../../ui/components/date-input/date-input";
import TableMui from "../../../../ui/components/table-mui/table-mui";
import { UserInfo } from "../../../../ui/models/userInfo";
import { ConvenioPractica } from "../../../models/convenio-practica";
import { ListModel } from "../../../models/list-model";
import { PracticaCreate } from "../../../models/practica-create";
import { PracticaSempre } from "../../../models/practica-sempre";
import { convenioServices } from "../../../services/convenio.services";
import { obrasocialServices } from "../../../services/obra-social.services";
import { practicaServices } from "../../../services/practica.services";
import { profesionalServices } from "../../../services/profesional.services";
import { handle400Error } from "../../../utils/handle-400-errors";
import { formatPracticaDescription } from "../../../utils/practica.utils";
import {
  initialProfesionalFilter,
  queryParamsProfesionalConfig,
} from "../../../utils/profesional.utils";
import { useQueryParamsReplace } from "../../../utils/useQueryParamsReplace";
import PracticaSempreModal from "../practica-sempre-modal/practica-sempre-modal";
import "./practica-add-modal.scss";

export interface PracticaAddModalProps {
  show: boolean;
  handleClose: (success: boolean, item: any) => void;
  refetch: () => void;
}

export function PracticaAddModal(props: Readonly<PracticaAddModalProps>) {
  const { show, handleClose, refetch } = props;
  const [obrasSociales, setObrasSociales] = useState<ListModel[]>([]);
  const [profesionales, setProfesionales] = useState<ListModel[]>([]);
  const [practicasConvenio, setPracticasConvenio] = useState<
    ConvenioPractica[]
  >([]);
  const [practicasConvenioLookup, setPracticasConvenioLookup] = useState<
    ListModel[]
  >([]);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [sempreId, setSempreId] = useState<string>();
  const [queryParamsProfesional, setQueryParamsProfesional] =
    useQueryParamsReplace(queryParamsProfesionalConfig);
  const [practicasToSave, setPracticasToSave] = useState<PracticaCreate[]>([]);
  const [showSempreModal, setShowSempreModal] = useState(false);
  const [sempreData, setSempreData] = useState<PracticaSempre>();
  const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);
  const [textAlertMessage, setTextAlertMessage] = useState<string>();
  const [fechaMaximaPractica, setFechaMaximaPractica] = useState<Date>();
  const [monthSelected, setMonthSelected] = useState<number>();
  const [yearSelected, setYearSelected] = useState<number>();

  const schema = yup
    .object()
    .shape({
      obraSocialId: yup.string().required("Campo requerido"),
      convenioPracticaPrecioId: yup.string().required("Campo requerido"),
      profesionalId: yup.string().required("Campo requerido"),
      fecha: yup.date().required("Campo requerido"),
      cantidad: yup
        .number()
        .typeError("Debe ser un número")
        .min(1, "1 Mínimo")
        .max(15, "15 Máximo")
        .required("Campo requerido"),
      paciente: yup.string().trim().max(250, "250 Máximo"),
      precio: yup
        .number()
        .typeError("Debe ser un número")
        .required("Campo requerido"),
      coseguro: yup
        .number()
        .typeError("Debe ser un número")
        .required("Campo requerido"),
    })
    .required();

  const {
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    control,
    setValue,
  } = useForm<PracticaCreate>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const authInfo = useSelector<RootState, UserInfo>(
    ({ auth }: any) => auth.info,
    shallowEqual
  );

  const handleSubmitAdd = (event: any) => {
    if (watch("obraSocialId") === sempreId && !sempreData)
      setShowSempreModal(true);
    else {
      const practicas: any = [];
      practicasToSave.forEach((p) => {
        const data: any = {
          convenioPracticaPrecioId: p.convenioPracticaPrecioId,
          cantidad: p.cantidad,
          fecha: p.fecha,
          paciente: sempreData
            ? sempreData.afiliadoDescripcion.split(" - ")[1]
            : p.paciente,
          profesionalId: p.profesionalId,
          precio: p.precio,
          practicaSempre: sempreData && {
            ...sempreData,
            formTipo:
              sempreData?.formTipo === "44" ? "4" : sempreData?.formTipo,
          },
        };
        data.fecha.setHours(0, 0, 0, 0);
        if (authInfo.role === "ADMIN") {
          if (monthSelected && yearSelected) {
            data.mesAno = new Date(yearSelected, monthSelected, 1);
            data.mesAno.setHours(0, 0, 0, 0);
          } else {
            setTextAlertMessage("Ingrese mes y año de facturación.");
            setShowAlertMessage(true);
            return;
          }
        }
        practicas.push(data);
      });

      practicaServices
        .create(practicas)
        .then((response: any) => {
          refetch();
          handleClose(true, response.data[0]);
        })
        .catch((error: any) => {
          if (error?.response?.status === 400) {
            handle400Error(error.response.data.errors, setError);
          } else {
            setTextAlertMessage(
              "No se pudo cargar la práctica. Intente nuevamente."
            );
            setShowAlertMessage(true);
          }
        });
    }
  };

  const checkDisabledButton = () => {
    if (!watch("obraSocialId") || !watch("fecha") || !watch("profesionalId"))
      return true;
    if (fechaMaximaPractica && watch("fecha") > fechaMaximaPractica)
      return true;
    if (practicasToSave.length === 0) return true;

    if (watch("obraSocialId") !== sempreId && !watch("paciente")) return true;

    return false;
  };

  const ActionsComponent = () => {
    return (
      <>
        <Button
          className="close-button"
          onClick={() => handleClose(false, null)}
        >
          Cancelar
        </Button>
        <Button
          className="create-button"
          variant="contained"
          onClick={handleSubmit((data) => handleSubmitAdd(data))}
          disabled={checkDisabledButton()}
        >
          Guardar
        </Button>
      </>
    );
  };

  const CalcularFechaMaxima = () => {
    const fechaActual = new Date();
    let fechaValidacion = new Date();
    const day = dayjs(fechaActual).date();
    let month = dayjs(fechaActual).month() + 1;
    if (day <= 7) month = month - 1;
    let date = new Date(dayjs(fechaValidacion).year(), month, 0);

    setFechaMaximaPractica(date);
  };

  useEffect(() => {
    getObrasSociales();
    CalcularFechaMaxima();
  }, []);

  useEffect(() => {
    if (watch("obraSocialId") && watch("fecha") && watch("profesionalId")) {
      resetForm();
      getPracticasConvenio();
    }
  }, [watch("obraSocialId"), watch("fecha"), watch("profesionalId")]);

  const resetForm = () => {
    setValue("convenioPracticaPrecioId", "", {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("cantidad", 0, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("paciente", "", {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("precio", 0, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("coseguro", 0, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    });

    setPracticasToSave([]);
  };

  const getObrasSociales = async () => {
    obrasocialServices
      .lookup()
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          if (element.value === "SEMPRE") setSempreId(element.id);
          items.push({
            value: element.id,
            label: element.value,
          });
        });
        setObrasSociales(items);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (authInfo) {
      if (authInfo.role === "PROFESIONAL") {
        setValue("profesionalId", authInfo.profesionalId, {
          shouldTouch: true,
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        getProfesionales("");
      }
    }
  }, [authInfo]);

  const getProfesionales = async (searchValue: string) => {
    setQueryParamsProfesional({
      ...initialProfesionalFilter,
      filter: searchValue,
    });
    profesionalServices
      .lookup(queryParamsProfesional)
      .then((response) => {
        const items: ListModel[] = [];
        response.data.forEach((element: any) => {
          items.push({
            value: element.id,
            label: element.value + " " + element.description,
          });
        });
        setProfesionales(items);
      })
      .catch((error) => {});
  };

  const getPracticasConvenio = async () => {
    const fecha = new Date(watch("fecha").toString());
    fecha.setHours(0, 0, 0, 0);
    convenioServices
      .getPracticasByOs(
        watch("obraSocialId"),
        fecha.toUTCString(),
        watch("profesionalId")
      )
      .then((response) => {
        const items: ListModel[] = [];
        response.data.practicas.forEach((element: any) => {
          items.push({
            value: element.convenioPracticaPrecioId,
            label: formatPracticaDescription(
              element.numeroPractica,
              element.descripcion
            ),
          });
        });
        setPracticasConvenioLookup(items);
        setPracticasConvenio(response.data.practicas);
        setDisabled(false);
      })
      .catch((error) => {
        setDisabled(true);
      });
  };

  const agregarPractica = () => {
    const fecha = new Date(watch("fecha").toString());
    fecha.setHours(0, 0, 0, 0);
    const practica = practicasConvenio.find(
      (p) => p.convenioPracticaPrecioId === watch("convenioPracticaPrecioId")
    );

    const newPractica: PracticaCreate = {
      profesionalId: watch("profesionalId"),
      convenioPracticaPrecioId: watch("convenioPracticaPrecioId"),
      codigoPractica: practica ? practica.numeroPractica : "",
      descripcionPractica: practica ? practica.descripcion : "",
      cantidad: watch("cantidad"),
      fecha: fecha,
      paciente: watch("paciente") ? watch("paciente") : "",
      precio: practica ? practica.precio : 0,
      coseguro: practica ? practica?.coseguro : 0,
      obraSocialId: watch("obraSocialId"),
    };

    setPracticasToSave([...practicasToSave, newPractica]);
  };

  const checkDisabledAgregar = () => {
    if (
      !watch("convenioPracticaPrecioId") ||
      !watch("cantidad") ||
      watch("cantidad") <= 0
    )
      return true;

    if (watch("obraSocialId") !== sempreId && !watch("paciente")) return true;
    return false;
  };

  const columns: TableColumn<PracticaCreate>[] = [
    {
      name: "Paciente",
      selector: (row) => row.paciente ?? "",
    },
    {
      name: "Código de Práctica",
      selector: (row) => row.codigoPractica ?? "",
    },
    {
      name: "Descripción",
      selector: (row) => row.descripcionPractica ?? "",
    },
    {
      name: "Cantidad",
      selector: (row) => row.cantidad,
    },
    {
      name: "Precio",
      selector: (row) => Number.parseFloat(row.precio.toString()).toFixed(2),
      wrap: true,
    },
    {
      name: "Coseguro",
      selector: (row) => Number.parseFloat(row.coseguro.toString()).toFixed(2),
    },
    {
      name: "Total",
      selector: (row) =>
        Number.parseFloat((row.precio * row.cantidad).toString()).toFixed(2),
    },
    {
      name: "",
      button: true,
      cell: (row) => (
        <Tooltip title="Eliminar">
          <IconButton aria-label="delete" onClick={() => handleDeleteRow(row)}>
            <DeleteOutlineOutlinedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleDeleteRow = (practica: PracticaCreate) => {
    const index = practicasToSave.findIndex(
      (p) => p.convenioPracticaPrecioId === practica.convenioPracticaPrecioId
    );
    if (index !== -1) {
      practicasToSave.splice(index, 1);
      setPracticasToSave([...practicasToSave]);
    }
  };

  const handleCloseSempreModal = (added: boolean, item: any) => {
    setShowSempreModal(false);
    if (added) {
      const afiliado = item.afiliadoDescripcion.split(" - ");

      setValue("paciente", afiliado[1], {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
      setSempreData(item);
    }
  };

  useEffect(() => {
    if (fechaMaximaPractica) {
      setMonthSelected(fechaMaximaPractica.getMonth());
      setYearSelected(fechaMaximaPractica.getFullYear());
    }
  }, [fechaMaximaPractica]);

  return (
    <CustomModal
      show={show}
      handleClose={() => handleClose(false, null)}
      title="Nueva Práctica"
      actions={<ActionsComponent />}
      size="lg"
    >
      <div className="practica-add-modal">
        {showAlertMessage && (
          <div className="error-message">
            <Alert variant="outlined" severity="error">
              {textAlertMessage}
            </Alert>
          </div>
        )}

        <Box
          sx={{ height: 400 }}
          component="form"
          autoComplete="off"
          onSubmit={handleSubmitAdd}
        >
          {authInfo.role === "ADMIN" && profesionales.length > 0 && (
            <div className="row">
              <div className="w-50">
                <AutocompleteSelect
                  name="profesionalId"
                  label="Profesional"
                  isMultiple={false}
                  register={register}
                  getFieldState={getFieldState}
                  formState={formState}
                  errors={errors}
                  required={true}
                  options={profesionales}
                  onChange={(value) =>
                    setValue("profesionalId", value, {
                      shouldTouch: true,
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                ></AutocompleteSelect>
              </div>
              <div className="w-50 m-2">
                <DateInputFilter
                  name="mesAno"
                  hiddenDay={true}
                  onChange={(value: Dayjs) => {
                    if (value !== null) {
                      setMonthSelected(value.get("month"));
                      setYearSelected(value.get("year"));
                    } else {
                      setMonthSelected(undefined);
                      setYearSelected(undefined);
                    }
                  }}
                  maxDate={dayjs()}
                  value={
                    monthSelected && yearSelected
                      ? dayjs(new Date(yearSelected, monthSelected, 1))
                      : undefined
                  }
                />
              </div>
            </div>
          )}
          {obrasSociales.length > 0 && fechaMaximaPractica && (
            <div className="row">
              <div className="w-20">
                <Controller
                  name="fecha"
                  control={control}
                  render={({ field }) => (
                    <DateInput
                      field={field}
                      name="fecha"
                      label="Fecha"
                      register={register}
                      getFieldState={getFieldState}
                      formState={formState}
                      errors={errors}
                      required={true}
                      value={field.value}
                      maxDate={fechaMaximaPractica}
                    ></DateInput>
                  )}
                />
              </div>
              <div className="w-80">
                <Controller
                  name="obraSocialId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      field={field}
                      name="obraSocialId"
                      label="Obra Social"
                      isMultiple={false}
                      register={register}
                      getFieldState={getFieldState}
                      formState={formState}
                      errors={errors}
                      required={true}
                      options={obrasSociales}
                    ></CustomSelect>
                  )}
                />
              </div>
            </div>
          )}
          <div className="row w-100">
            <CustomInput
              name="paciente"
              label="Paciente"
              type="text"
              register={register}
              getFieldState={getFieldState}
              formState={formState}
              errors={errors}
              required={true}
              maxLength={250}
              disabled={disabled}
              assistiveText="Si es paciente de Sempre no es necesario completar"
            ></CustomInput>
          </div>

          <div className="row w-100">
            <div className="w-70">
              <AutocompleteSelect
                name="convenioPracticaPrecioId"
                label="Práctica"
                isMultiple={false}
                register={register}
                getFieldState={getFieldState}
                formState={formState}
                errors={errors}
                required={true}
                options={practicasConvenioLookup}
                disabled={disabled}
                onChange={(value) =>
                  setValue("convenioPracticaPrecioId", value, {
                    shouldTouch: true,
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              ></AutocompleteSelect>
            </div>
            <div className="w-30 flex">
              <CustomInput
                name="cantidad"
                label="Cantidad"
                type="number"
                register={register}
                getFieldState={getFieldState}
                formState={formState}
                errors={errors}
                required={true}
                max={90}
                min={1}
                disabled={disabled}
              ></CustomInput>
              <Button
                className="close-button"
                onClick={() => agregarPractica()}
                disabled={checkDisabledAgregar()}
              >
                Agregar
              </Button>
            </div>
          </div>

          {practicasToSave.length > 0 && (
            <div className="row w-100">
              <TableMui
                table={{
                  columns,
                  data: practicasToSave,
                  paginationTotalRows: practicasToSave.length,
                  pointerOnHover: true,
                  highlightOnHover: true,
                }}
              ></TableMui>
            </div>
          )}
        </Box>
        {!!showSempreModal && (
          <PracticaSempreModal
            show={showSempreModal}
            handleClose={handleCloseSempreModal}
          />
        )}
      </div>
    </CustomModal>
  );
}

export default PracticaAddModal;
